import { ApolloClient } from '@apollo/client';
import { ErrorResponse } from '@apollo/client/link/error';
import { ServerError } from 'apollo-link-http-common';
import { generatePath, Location, NavigateFunction } from 'react-router-dom';

import {
  PUBLIC_HOME,
  PUBLIC_SESSION_TAKEN,
  PUBLIC_STUDENT_SIGNIN,
} from 'global/public-routes.constants';
import {
  PRIVATE_CLASS,
  PRIVATE_CLASS_BASE,
  PRIVATE_SIGNIN_REFRESH_SESSION,
  PRIVATE_STUDENT_CLASSES,
  PRIVATE_STUDENTS,
} from 'global/private-routes.constants';
import {
  setUserData,
  isSignedIn,
  getAlreadySignedInOnce,
  getUserData,
  getUserId,
} from 'approot/shared/api/auth/auth';
import {
  getStudentUserData,
  setStudentUserData,
} from 'approot/shared/api/auth/student-user-auth';
import { updateAnalyticsDataLayer } from 'approot/shared/analytics/data-layer';
import { GET_USER } from 'approot/shared/user/user-data.graphql';
import { signOut, studentSignOut } from 'approot/shared/signin/signin.utils';
import {
  clearStudentSession,
  getStudentData,
  getStudentToken,
  getStudentUserToken,
} from 'approot/student-class-to-do/student-class-to-do.utils';
import {
  isExpiredClassToDoTokenServerError,
  isForbidden,
  isStaleSessionServerError,
  isStaleStudentSessionServerError,
  isUnauthorised,
} from 'approot/errors/error.utils';
import {
  ERROR_MESSAGE_CLASS_ARCHIVED,
  ERROR_MESSAGE_REMOVED_FROM_CLASS,
  GRAPHQL_REQUEST_TRACE_ID,
} from 'global/constants';
import {
  EVENT_CATEGORY_AUTHENTICATION,
  EVENT_ACTION_SESSION_TAKEN,
  EVENT_TARGET_TYPE_TEACHER,
  EVENT_TARGET_TYPE_STUDENT,
} from 'approot/shared/data-tracker/data-events.constants';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import { GET_STUDENT } from 'approot/students/students.graphql';
import type { GetStudentQuery } from './approot/students/students.graphql.generated';
import { globalClassesNotificationVar } from 'approot/global-notifications/classes/global-classes-notification.apollo';
import { logException } from 'approot/shared/debug';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { generateTraceId } from 'approot/shared/api/auth/generate-trace-id';
import { isStudentAuthenticated } from 'approot/students/logic/is-student-authenticated';
import { signInStateVar } from 'approot/shared/user/user-data.apollo';
import { initialiseSessionStorageDuration } from 'lib/session/session-storage-duration';
import { RememberType } from 'approot/shared/signin/signin.constants';
import { isValidStudentRedirectPath } from 'approot/shared/signin/logic/is-valid-student-redirect-path';
import { navigateTo } from 'lib/session/session-navigation';
import { setStudentClassFromUrl } from 'approot/shared/signin/logic/set-student-class-from-url';
import { isInteractivePath } from 'approot/shared/signin/logic/is-interactive-path';
import { isValidStudentClassToDoPath } from 'approot/shared/signin/logic/is-valid-student-class-to-do-path';

const SESSION_TAKEN_STATUS_CODE = 419;

export function getClientStateDefaults() {
  const userData = getUserData();
  const studentUserData = getStudentUserData();

  const isStudentSignedIn = !!getStudentUserToken() || !!getStudentToken();
  const isTeacherSignedIn = isSignedIn();
  const isAlreadySignedInOnce = getAlreadySignedInOnce();

  signInStateVar({
    isSignedIn: isTeacherSignedIn,
    isAlreadySignedInOnce,
    isStudentSignedIn,
  });

  initialiseSessionStorageDuration();

  return {
    isSignedIn: isTeacherSignedIn,
    isAlreadySignedInOnce,
    isStudentSignedIn,
    user: userData && userData.user,
    student: studentUserData?.studentUser,
  };
}

export function fetchUserIfSessionIsActive(client: ApolloClient<any>) {
  const userData = getUserData();

  if (userData && userData.token) {
    updateAnalyticsDataLayer(userData.user);
    const token = userData.token;

    // redirect a teacher away from student pages if they are signed in
    if (
      !isInteractivePath(window.location.pathname) &&
      isValidStudentRedirectPath(window.location.pathname)
    ) {
      navigateTo(PUBLIC_HOME, { replace: true });
    }

    client
      .query({
        query: GET_USER,
        fetchPolicy: 'network-only',
        context: {
          headers: {
            authorization: `Bearer ${token}`,
            [GRAPHQL_REQUEST_TRACE_ID]: generateTraceId(),
          },
        },
      })
      .then(res => {
        if (res?.data?.user) {
          setUserData(
            {
              token,
              user: res.data.user,
            },
            RememberType.LocalStorage
          );
        } else {
          logException({
            sourceFile: 'apollo.utils.tsx',
            message: `There was an issue querying for user data. Response status  was: ${res.networkStatus}`,
            statusCode: res.networkStatus,
          });
        }
      })
      .catch(error => {
        if (isUnauthorised(error) || isForbidden(error)) {
          signOut({
            signOutServerSide: false,
          });
          navigateTo(PUBLIC_HOME, { replace: true });
        }
      });
  }
}

export function fetchStudentUserIfSessionIsActive(client: ApolloClient<any>) {
  // If there is an "unauthenticated" student, we need to redirect them to the class page
  const unauthenticatedStudentData = getStudentData();
  if (unauthenticatedStudentData.token) {
    if (
      !isInteractivePath(window.location.pathname) &&
      !isValidStudentClassToDoPath(window.location.pathname)
    ) {
      navigateTo(
        generatePath(PRIVATE_CLASS, {
          classId: unauthenticatedStudentData.classId || '',
        }),
        {
          replace: true,
        }
      );
    }
  }

  const studentUserData = getStudentUserData();

  // If there is an "stale" student, we need to sign them out
  if (studentUserData && !studentUserData.studentUser) {
    studentSignOut();
    return;
  }

  // If there is an "authenticated" student, we need to fetch their data
  if (studentUserData && studentUserData.token) {
    const token = studentUserData.token;

    // redirect student away from teacher pages if they are signed in
    if (
      !isInteractivePath(window.location.pathname) &&
      !isValidStudentRedirectPath(window.location.pathname)
    ) {
      navigateTo(PRIVATE_STUDENT_CLASSES, { replace: true });
    }

    client
      .query<GetStudentQuery, null>({
        query: GET_STUDENT,
        fetchPolicy: 'network-only',
        context: {
          headers: {
            authorization: `Bearer ${token}`,
            [GRAPHQL_REQUEST_TRACE_ID]: generateTraceId(),
          },
        },
      })
      .then(res => {
        const studentData = res.data.getStudent;
        if (studentData) {
          setStudentUserData({
            token,
            studentUser: studentData,
          });
          setStudentClassFromUrl(studentData.currentClasses);
        }
      })
      .catch(error => {
        if (isUnauthorised(error) || isForbidden(error)) {
          studentSignOut({
            signOutServerSide: false,
          });
          navigateTo(PRIVATE_STUDENTS, { replace: true });
        }
      });
  }
}

export function checkIfSessionIsTaken(
  error: ErrorResponse,
  location?: Location,
  navigate?: NavigateFunction
) {
  const networkError = error.networkError;
  if (!networkError) {
    return;
  }

  const statusCode = (networkError as ServerError).statusCode;
  switch (statusCode) {
    case SESSION_TAKEN_STATUS_CODE: {
      if (isSignedIn()) {
        navigate?.(PUBLIC_SESSION_TAKEN, { replace: true });
        pushDataTrackerEvent(
          EVENT_CATEGORY_AUTHENTICATION,
          EVENT_ACTION_SESSION_TAKEN,
          {
            target_type: EVENT_TARGET_TYPE_TEACHER,
            target_identifier: getUserId(),
          }
        );
        // try and avoid apollo error: Store reset while query was in flight (not completed in link chain)
        setTimeout(() =>
          signOut({
            signOutServerSide: false,
          })
        );
      } else if (isStudentAuthenticated()) {
        const studentUser = getStudentUserData();
        navigate?.(PUBLIC_SESSION_TAKEN, { replace: true });
        pushDataTrackerEvent(
          EVENT_CATEGORY_AUTHENTICATION,
          EVENT_ACTION_SESSION_TAKEN,
          {
            target_type: EVENT_TARGET_TYPE_STUDENT,
            target_identifier: studentUser?.studentUser.id,
          }
        );
        // try and avoid apollo error: Store reset while query was in flight (not completed in link chain)
        setTimeout(() =>
          studentSignOut({
            signOutServerSide: false,
          })
        );
      }
      break;
    }
    default: {
      // pass
    }
  }
}

export function checkForCurrentClassError(error: ErrorResponse) {
  const networkError = error.networkError;
  if (!networkError) {
    return;
  }

  const classError = (networkError as any)?.result?.errors?.find(
    (e: any) =>
      e.message === ERROR_MESSAGE_CLASS_ARCHIVED ||
      e.message === ERROR_MESSAGE_REMOVED_FROM_CLASS
  );

  if (classError) {
    globalClassesNotificationVar(classError.message);
  }
}

export function checkForInvalidClassToDoToken(error: ErrorResponse) {
  const networkError = error.networkError;
  if (!networkError) {
    return;
  }

  const path = window.location.pathname;

  if (
    path.includes(`${PRIVATE_CLASS_BASE}/`) &&
    isExpiredClassToDoTokenServerError(networkError as ServerError)
  ) {
    clearStudentSession();
    window.location.replace(getRegionalisedLink(PUBLIC_STUDENT_SIGNIN));
  }
}

export function checkIfSessionIsStale(error: ErrorResponse) {
  const networkError = error.networkError;
  if (!networkError) {
    return;
  }

  if (isStaleSessionServerError(networkError as ServerError | undefined)) {
    window.location.replace(
      getRegionalisedLink(PRIVATE_SIGNIN_REFRESH_SESSION)
    );
  }

  if (
    isStaleStudentSessionServerError(networkError as ServerError | undefined)
  ) {
    window.location.replace(getRegionalisedLink(PRIVATE_STUDENTS));
  }
}
